import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getPost, createPost, updatePost, getCategories, uploadImage } from '@/lib/api';
import { slugify } from '@/lib/utils';
import { toast } from 'sonner';
import { Save, ArrowLeft, Upload, X } from 'lucide-react';
import { Editor } from '@tinymce/tinymce-react';

const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

const AdminBlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    category_id: '',
    tags: [],
    seo_title: '',
    seo_description: '',
    published: false
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesRes = await getCategories();
        setCategories(categoriesRes.data);

        if (id) {
          const postRes = await getPost(id);
          setFormData({
            ...postRes.data,
            category_id: postRes.data.category_id || ''
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from title
    if (field === 'title' && !id) {
      setFormData(prev => ({ ...prev, slug: slugify(value) }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const response = await uploadImage(file);
      handleChange('featured_image', response.data.url);
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const content = editorRef.current ? editorRef.current.getContent() : formData.content;
    const dataToSave = { ...formData, content };

    try {
      if (id) {
        await updatePost(id, dataToSave);
        toast.success('Post updated successfully');
      } else {
        await createPost(dataToSave);
        toast.success('Post created successfully');
      }
      navigate('/admin/blog');
    } catch (error) {
      toast.error('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-blue border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-blog-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/blog')}
            data-testid="back-btn"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back
          </Button>
          <h1 className="font-heading text-2xl font-bold text-brand-blue">
            {id ? 'Edit Post' : 'New Post'}
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.published}
              onCheckedChange={(checked) => handleChange('published', checked)}
              data-testid="publish-switch"
            />
            <Label>{formData.published ? 'Published' : 'Draft'}</Label>
          </div>
          <Button 
            onClick={handleSubmit}
            disabled={saving}
            className="bg-brand-orange hover:bg-brand-orange/90 text-white"
            data-testid="save-btn"
          >
            <Save size={18} className="mr-2" />
            {saving ? 'Saving...' : 'Save Post'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-white rounded-xl">
            <CardContent className="p-6 space-y-6">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Enter post title"
                  className="text-lg"
                  required
                  data-testid="title-input"
                />
              </div>

              <div>
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => handleChange('slug', e.target.value)}
                  placeholder="post-url-slug"
                  data-testid="slug-input"
                />
              </div>

              <div>
                <Label htmlFor="excerpt">Excerpt *</Label>
                <Input
                  id="excerpt"
                  value={formData.excerpt}
                  onChange={(e) => handleChange('excerpt', e.target.value)}
                  placeholder="Brief description of the post"
                  required
                  data-testid="excerpt-input"
                />
              </div>

              <div>
                <Label>Content *</Label>
                <div className="border rounded-lg overflow-hidden">
                  <Editor
                    apiKey={TINYMCE_API_KEY}
                    onInit={(_evt, editor) => editorRef.current = editor}
                    initialValue={formData.content}
                    init={{
                      height: 500,
                      menubar: true,
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'help', 'wordcount'
                      ],
                      toolbar: 'undo redo | blocks | ' +
                        'bold italic forecolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | ' +
                        'removeformat | link image table | help',
                      content_style: 'body { font-family: Inter, sans-serif; font-size: 16px; }'
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Featured Image */}
          <Card className="bg-white rounded-xl">
            <CardContent className="p-6">
              <Label>Featured Image</Label>
              {formData.featured_image ? (
                <div className="mt-2 relative">
                  <img 
                    src={formData.featured_image} 
                    alt="Featured" 
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => handleChange('featured_image', '')}
                  >
                    <X size={16} />
                  </Button>
                </div>
              ) : (
                <label className="mt-2 flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload size={32} className="text-gray-400 mb-2" />
                  <span className="text-gray-500 text-sm">Click to upload</span>
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*"
                    onChange={handleImageUpload}
                    data-testid="image-upload-input"
                  />
                </label>
              )}
            </CardContent>
          </Card>

          {/* Category */}
          <Card className="bg-white rounded-xl">
            <CardContent className="p-6">
              <Label>Category</Label>
              <Select 
                value={formData.category_id} 
                onValueChange={(value) => handleChange('category_id', value)}
              >
                <SelectTrigger className="mt-2" data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card className="bg-white rounded-xl">
            <CardContent className="p-6">
              <Label>Tags</Label>
              <div className="mt-2 flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  data-testid="tag-input"
                />
                <Button type="button" onClick={handleAddTag} variant="outline">Add</Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-brand-teal/10 text-brand-teal rounded-full text-sm"
                    >
                      {tag}
                      <button 
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 hover:text-red-500"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* SEO */}
          <Card className="bg-white rounded-xl">
            <CardContent className="p-6 space-y-4">
              <h3 className="font-heading font-bold text-brand-blue">SEO Settings</h3>
              <div>
                <Label htmlFor="seo_title">SEO Title</Label>
                <Input
                  id="seo_title"
                  value={formData.seo_title}
                  onChange={(e) => handleChange('seo_title', e.target.value)}
                  placeholder="SEO title (optional)"
                  data-testid="seo-title-input"
                />
              </div>
              <div>
                <Label htmlFor="seo_description">SEO Description</Label>
                <Input
                  id="seo_description"
                  value={formData.seo_description}
                  onChange={(e) => handleChange('seo_description', e.target.value)}
                  placeholder="SEO description (optional)"
                  data-testid="seo-desc-input"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogEditor;
